// Toplu dolap duzenle dialog'u — disardan secili ID'leri alir, secili alanlari
// (boyut/periyot/ucret) onlara uygular.

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { CheckSquare, Square, X } from 'lucide-react';
import { Buton, Etiket, Girdi, Rozet, toast } from '@emanetx/ui';
import { useDolapTopluGuncelle } from './api.js';

type Boyut = 'KUCUK' | 'ORTA' | 'BUYUK' | 'EKSTRA';

export function TopluDuzenleDialog({
  acik,
  onDegisim,
  noktaKod,
  noktaAd,
  seciliIdler,
  onTamamlandi,
}: {
  acik: boolean;
  onDegisim: (v: boolean) => void;
  noktaId: string;
  noktaKod: string;
  noktaAd: string;
  seciliIdler: string[];
  onTamamlandi?: () => void;
}) {
  const [boyutAcik, setBoyutAcik] = useState(false);
  const [boyut, setBoyut] = useState<Boyut>('ORTA');
  const [kiralamaSaatiAcik, setKiralamaSaatiAcik] = useState(false);
  const [kiralamaSaati, setKiralamaSaati] = useState(4);
  const [periyotAcik, setPeriyotAcik] = useState(false);
  const [periyotUcreti, setPeriyotUcreti] = useState(50);
  const [saatlikAcik, setSaatlikAcik] = useState(false);
  const [saatlikUcret, setSaatlikUcret] = useState(10);
  const [cezaAcik, setCezaAcik] = useState(false);
  const [cezaUcreti, setCezaUcreti] = useState(100);

  const guncelle = useDolapTopluGuncelle();

  const onayla = async () => {
    if (seciliIdler.length === 0) {
      toast.error('En az 1 dolap seçin.');
      return;
    }
    const veri: Record<string, unknown> = {};
    if (boyutAcik) veri.boyut = boyut;
    if (kiralamaSaatiAcik) veri.kiralamaSaati = kiralamaSaati;
    if (periyotAcik) veri.periyotUcreti = periyotUcreti;
    if (saatlikAcik) veri.saatlikUcret = saatlikUcret;
    if (cezaAcik) veri.cezaUcreti = cezaUcreti;
    if (Object.keys(veri).length === 0) {
      toast.error('En az 1 alanın "uygula" kutusunu işaretleyin.');
      return;
    }
    try {
      const r = await guncelle.mutateAsync({ idler: seciliIdler, ...veri });
      toast.success(`${r.guncellenen} dolap güncellendi`);
      onTamamlandi?.();
      onDegisim(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <Dialog.Root open={acik} onOpenChange={onDegisim}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 w-[94vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-fume-900 dark:text-fume-50 text-base font-semibold">
                Toplu Düzenle
              </Dialog.Title>
              <Dialog.Description asChild>
                <div className="text-fume-500 dark:text-fume-400 mt-1.5 flex flex-wrap items-center gap-2 text-sm">
                  <span className="truncate">{noktaAd}</span>
                  <Rozet ton="fume" className="font-mono">
                    {noktaKod}
                  </Rozet>
                  <Rozet ton="mavi" className="font-semibold">
                    {seciliIdler.length} dolap seçili
                  </Rozet>
                </div>
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button className="text-fume-400 hover:text-fume-700" aria-label="Kapat">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <p className="text-fume-500 dark:text-fume-400 mb-3 mt-5 text-xs">
            Uygulanacak alanların kutusunu işaretle, değeri gir.
          </p>

          <div className="space-y-3">
            <Satir
              acik={boyutAcik}
              onAcik={setBoyutAcik}
              label="Boyut"
              alan={
                <select
                  value={boyut}
                  onChange={(e) => setBoyut(e.target.value as Boyut)}
                  disabled={!boyutAcik}
                  className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-9 w-full rounded-lg border bg-white px-2 text-xs focus:outline-none focus:ring-2 disabled:opacity-50"
                >
                  <option value="KUCUK">Küçük (S)</option>
                  <option value="ORTA">Orta (M)</option>
                  <option value="BUYUK">Büyük (L)</option>
                  <option value="EKSTRA">Ekstra (XL)</option>
                </select>
              }
            />
            <Satir
              acik={kiralamaSaatiAcik}
              onAcik={setKiralamaSaatiAcik}
              label="Periyot (saat)"
              alan={
                <Girdi
                  type="number"
                  min={1}
                  disabled={!kiralamaSaatiAcik}
                  value={kiralamaSaati}
                  onChange={(e) => setKiralamaSaati(Number(e.target.value) || 1)}
                />
              }
            />
            <Satir
              acik={periyotAcik}
              onAcik={setPeriyotAcik}
              label="Periyot ücreti"
              alan={
                <Girdi
                  type="number"
                  step="0.01"
                  disabled={!periyotAcik}
                  value={periyotUcreti}
                  onChange={(e) => setPeriyotUcreti(Number(e.target.value) || 0)}
                />
              }
            />
            <Satir
              acik={saatlikAcik}
              onAcik={setSaatlikAcik}
              label="Saatlik aşım"
              alan={
                <Girdi
                  type="number"
                  step="0.01"
                  disabled={!saatlikAcik}
                  value={saatlikUcret}
                  onChange={(e) => setSaatlikUcret(Number(e.target.value) || 0)}
                />
              }
            />
            <Satir
              acik={cezaAcik}
              onAcik={setCezaAcik}
              label="Ceza"
              alan={
                <Girdi
                  type="number"
                  step="0.01"
                  disabled={!cezaAcik}
                  value={cezaUcreti}
                  onChange={(e) => setCezaUcreti(Number(e.target.value) || 0)}
                />
              }
            />
          </div>

          <div className="flex flex-col-reverse gap-2 pt-5 sm:flex-row sm:justify-end">
            <Dialog.Close asChild>
              <Buton stil="cizgili" disabled={guncelle.isPending}>
                Vazgeç
              </Buton>
            </Dialog.Close>
            <Buton
              onClick={() => void onayla()}
              disabled={guncelle.isPending || seciliIdler.length === 0}
            >
              {guncelle.isPending ? 'Uygulanıyor…' : `${seciliIdler.length} Dolaba Uygula`}
            </Buton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Satir({
  acik,
  onAcik,
  label,
  alan,
}: {
  acik: boolean;
  onAcik: (v: boolean) => void;
  label: string;
  alan: React.ReactNode;
}) {
  return (
    <div className="flex items-end gap-2">
      <button
        type="button"
        onClick={() => onAcik(!acik)}
        className="mb-2.5 flex-shrink-0"
        aria-label={`${label} uygula`}
      >
        {acik ? (
          <CheckSquare size={16} className="text-marka-600" />
        ) : (
          <Square size={16} className="text-fume-400" />
        )}
      </button>
      <div className="flex-1">
        <Etiket>{label}</Etiket>
        <div className="mt-1">{alan}</div>
      </div>
    </div>
  );
}
